import type { EventContext } from '../../utils/createEventingPayloadsDE';
import type { CustomDimensions } from '../../types/types';
import {
  isStudyMatchResultsSource,
  RESULTS_FORMAT_SUBTYPE,
  UNQUALIFIED_FORMAT_SUBTYPE,
} from '../../utils/configurable-step/offeringsResultsSourceIdentity';

/**
 * Partial {@link EventContext} carrying the per-card product identity a
 * `kind: 'product'` Trackable threads onto its `ProductLoaded` /
 * `ProductViewed` payloads.
 *
 * Why this exists: a DSL result card (`DegreeCardDetailed`,
 * registered as `kind: 'product'`) auto-emits product events via the
 * `Trackable` path, but `Trackable` only ever had the authored
 * `_meta.tracking.*` slice and the step correlation — it had no access
 * to the per-row product data. The result was a `ProductViewed` with an
 * empty product object (`brand`/`name`/`variant`/`category`/`productId`/
 * `sku` blank, `formatType: 'dexp-entry-component'`, `location:
 * 'aboveTheFold'`). The imperative `Card` derives the same fields from
 * the `Result` row via `createResultsEventContext`; this helper mirrors
 * that mapping for the DSL render path off the repeater item scope so
 * the two paths reach product parity without per-config authoring.
 */
export type ProductIdentityContext = Partial<EventContext>;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

/**
 * Map an `id` / `cap`-style numeric (or numeric-string) field to the
 * string form Tagular expects (`productId` / `sku`). Returns `undefined`
 * for nullish / empty so the spread in the payload builder drops the key
 * entirely (parity with the imperative `capId ? String(capId) :
 * undefined` mapping).
 */
const asId = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number')
    return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value === 'string') return value;
  return undefined;
};

/**
 * Derive the 1-based card position from the repeater row index path
 * (`useItemScopeIndexPath`, e.g. `"0"`, `"2"`, or `"1.3"` under
 * nesting). Mirrors the imperative `index + 1`. Returns `undefined` when
 * the node is not inside a repeater so the payload omits `position`.
 */
const positionFromIndexPath = (indexPath?: string): number | undefined => {
  if (!indexPath) return undefined;
  const segments = indexPath.split('.');
  const leaf = Number(segments[segments.length - 1]);
  return Number.isFinite(leaf) ? leaf + 1 : undefined;
};

/**
 * Build the product-identity override for a `kind: 'product'` Trackable
 * from the enclosing repeater item (a `Result` row) and its row index.
 *
 * Returns `undefined` when the item is not a result-shaped object (no
 * `schoolSlug` and no `programId`) so non-result product nodes — and
 * product nodes rendered outside a repeater — are left untouched and
 * keep their prior behaviour.
 *
 * Field mapping (mirrors the imperative `Card` →
 * `createResultsEventContext`):
 *   - `brand`        ← `schoolSlug`
 *   - `productId`    ← `programId`
 *   - `sku`          ← `capId`
 *   - `presets`      ← `{ degree: degreeSlug, category: categorySlug, subject: subjectSlug }`
 *                      (the payload builders surface these as the product
 *                      `variant` / `category` / `name`)
 *   - `viewCorrelationId` ← `viewCorrelationId`
 *   - `productClickedCorrelationId` ← `productClickedCorrelationId`
 *                      (so a DSL `ProductClicked` rides the selected
 *                      result's click correlation rather than falling
 *                      back to the flow `formViewedCorrelationId`)
 *   - `listId`       ← `listId`
 *   - `position`     ← repeater row index + 1
 *   - `customDimensions` ← `[{ key: 'resultsSource', value }]` when the
 *                      row carries `resultsSource`; merged with the
 *                      StepCorrelation/authored dimensions in `Trackable`.
 *   - `formatType`   ← `'dexp-offering'` (offering product surface)
 *   - `formatSubtype`← `'dexp-results'`, or `'dexp-unqualified'` when
 *                      `resultsSource` is the flat StudyMatch endpoint
 *
 * `location` is NOT set here — it lives on the `DegreeCardDetailed` registry
 * entry, so EL cards fall through to the Monarch slot placement.
 */
export function buildProductIdentityFromItem(
  item: unknown,
  indexPath?: string,
): ProductIdentityContext | undefined {
  if (!isPlainObject(item)) return undefined;

  const brand = asString(item.schoolSlug);
  const productId = asId(item.programId);
  if (brand === undefined && productId === undefined) {
    // Not a result row — leave the payload to its prior defaults.
    return undefined;
  }

  const resultsSource = asString(item.resultsSource);
  const out: ProductIdentityContext = {
    formatType: 'dexp-offering',
    formatSubtype: isStudyMatchResultsSource(resultsSource)
      ? UNQUALIFIED_FORMAT_SUBTYPE
      : RESULTS_FORMAT_SUBTYPE,
  };

  if (brand !== undefined) out.brand = brand;
  if (productId !== undefined) out.productId = productId;

  const sku = asId(item.capId);
  if (sku !== undefined) out.sku = sku;

  const viewCorrelationId = asString(item.viewCorrelationId);
  if (viewCorrelationId !== undefined)
    out.viewCorrelationId = viewCorrelationId;

  const productClickedCorrelationId = asString(
    item.productClickedCorrelationId,
  );
  if (productClickedCorrelationId !== undefined)
    out.productClickedCorrelationId = productClickedCorrelationId;

  const listId = asString(item.listId);
  if (listId !== undefined) out.listId = listId;

  const degree = asString(item.degreeSlug);
  const category = asString(item.categorySlug);
  const subject = asString(item.subjectSlug);
  if (degree !== undefined || category !== undefined || subject !== undefined) {
    out.presets = { degree, category, subject };
  }

  const position = positionFromIndexPath(indexPath);
  if (position !== undefined) out.position = position;

  // Carry the serving Offerings endpoint (`/match` vs `/search`) as a product
  // dimension. Stamped by `normalizeOfferingsMatchItem`; absent otherwise.
  if (resultsSource !== undefined) {
    out.customDimensions = [{ key: 'resultsSource', value: resultsSource }];
  }

  return out;
}

// Merge product `customDimensions` in order: step dims → identity dims
// (`resultsSource`) → extras. Shared by the click and load/view payloads so they
// stay in lockstep. Returns `stepDimensions` unchanged when there's nothing to add.
export function mergeProductCustomDimensions(
  stepDimensions: CustomDimensions,
  productContext: ProductIdentityContext | undefined,
  extraDims: CustomDimensions,
): CustomDimensions {
  const identityDims = productContext?.customDimensions ?? [];
  if (identityDims.length === 0 && extraDims.length === 0) {
    return stepDimensions;
  }
  return [...stepDimensions, ...identityDims, ...extraDims];
}
